import React, { useEffect, useState } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { useBasket } from 'context/BasketContext';
import { useBookmark } from 'context/BookmarkContext';
import {
  BringzzRecommendationItem,
  BringzzProductBox,
  BringzzCartSelector,
  BringzzIcon,
  BringzzText,
  BringzzBoxSlider,
  BringzzBasketDrawer,
  BringzzBottomDrawer,
  BringzzShimmer,
  BringzzInput,
  BringzzButton
} from "@bringzz/components";
import useRequest from "../../hooks/useRequest";
import RecommendationItemBackground from 'assets/images/RecommendationItemBackground.png';
import DefaultProductImage from 'assets/DefaultProductImage.png';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { USER_ROLES } from 'utils/constants';

const RecommendationBox = ({ className, ...props }) => {
	const { sendRequest, loading } = useRequest();
	const { addToBasket } = useBasket();
	const { getBookmarkStatus, getBookmarkStatuses, toggleBookmark, collections, createCollection, isBookmarked, removeBookmark } = useBookmark();
	const [featuredProducts, setFeaturedProducts] = useState([]);
	const [bookmarkStatuses, setBookmarkStatuses] = useState({});
	const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);
	const [showQuantityModal, setShowQuantityModal] = useState(false);
	const [currentProduct, setCurrentProduct] = useState(null);
	const [currentQuantity, setCurrentQuantity] = useState(0);
	const [addedProduct, setAddedProduct] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const { navigate } = useNavigation();
	const { user } = useAuth();
	const { t } = useTranslation();
	
	// Bookmark collection states
	const [showBookmarkDrawer, setShowBookmarkDrawer] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [newCollectionName, setNewCollectionName] = useState('');
	const [showNewCollectionInput, setShowNewCollectionInput] = useState(false);

  // No need for separate bookmark fetching - handled by BookmarkContext

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      setIsLoading(true);
      try {
        const response = await sendRequest({
          method: 'GET',
          url: '/module/pharmacyproduct/getFeaturedProducts'
        });

        if (response?.data) {
          console.log('=== featuredProducts ===');
          console.log(response.data);

          if (Array.isArray(response.data)) {
            setFeaturedProducts(response.data);
            
            const bookmarkStatuses = {};
            response.data.forEach(product => {
              bookmarkStatuses[product.integrationId] = product.isBookmarked ? 'success' : 'error';
            });
            setBookmarkStatuses(bookmarkStatuses);
          }
        }
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Bookmark status checking now handled by BookmarkContext

const addBookmarkToCollection = async (productUUID, collectionUuid = null) => {
  const success = await toggleBookmark(productUUID, 1, collectionUuid);
  if (success) {
    // Update the product's bookmark status locally
    setFeaturedProducts(prev => prev.map(p => 
      p.integrationId === productUUID ? { ...p, isBookmarked: true } : p
    ));
    setShowBookmarkDrawer(false);
    setSelectedProductId(null);
    const bookmarkUrl = collectionUuid ? `/settings/bookmarks/collection?id=${collectionUuid}&tab=products` : '/settings/bookmarks';
    toast.success(
      <div>
        {t('component.bookmarks.bookmark_success')}{' '}
        <span 
          onClick={() => navigate(bookmarkUrl)}
          className="underline cursor-pointer text-blue-600 hover:text-blue-800"
        >
          View bookmarks
        </span>
      </div>
    );
  } else {
    toast.error(t('component.bookmarks.bookmark_error'));
  }
};

const createNewCollection = async () => {
  if (!newCollectionName.trim()) return;
  
  const success = await createCollection(newCollectionName);
  if (success) {
    setNewCollectionName('');
    setShowNewCollectionInput(false);
    toast.success(t('component.bookmarks.collection_created'));
  } else {
    toast.error(t('component.bookmarks.bookmark_error'));
  }
};

const handleBookmark = async (event, productUUID) => {
  event.stopPropagation();
  
  if (!user) {
    navigate('/login');
    return;
  }
  
  const product = featuredProducts.find(p => p.integrationId === productUUID);
  
  if (product?.isBookmarked) {
    const success = await removeBookmark(productUUID, 1);
    if (success) {
      // Update the product's bookmark status locally
      setFeaturedProducts(prev => prev.map(p => 
        p.integrationId === productUUID ? { ...p, isBookmarked: false } : p
      ));
      toast.success(t('component.bookmarks.bookmark_removed'));
    }
  } else {
    setSelectedProductId(productUUID);
    setShowBookmarkDrawer(true);
  }
};

  const addToCart = async (productId, quantity) => {
    const product = featuredProducts.find(p => p.productId === productId);
    if (!product) return;

    if (quantity > product.quantity) {
      setCurrentProduct(product);
      setCurrentQuantity(quantity);
      setShowQuantityModal(true);
      return;
    }

    try {
      const result = await addToBasket({
        integrationId: product.integrationId,
        productIntegrationId: product.integrationId,
        quantity: quantity,
        price: product.price,
        imgUrl: product.productImage,
        manufacturer: product.pharmacyId_Dyn_Sort,
        name: product.productName,
        amount: `${product.productAmount} ${product.packagingType}`
      });

      if (result?.status === 'success') {
        setAddedProduct({
          ...product,
          quantity: quantity,
          price: product.price,
          imgUrl: product.productImage,
          manufacturer: product.pharmacyId_Dyn_Sort,
          name: product.productName,
          amount: `${product.productAmount} ${product.packagingType}`
        });
        setIsBasketDrawerOpen(true);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
	};
	
	  const handleContinueShopping = () => {
		setIsBasketDrawerOpen(false);
		// Keep the added product so it shows correctly when reopened
	  };
	
	  const handleGoToBasket = () => {
		navigate("/basket");
	  };

	const clickOnProduct = productId => {
		console.log(`Product ${productId} clicked`);
	};

	const doFav = (event, productId) => {
		event.stopPropagation();
		console.log(`${productId} bookmarked`);
	};

const ProductBoxShimmer = () => (
  <div className="flex flex-col min-w-40 max-w-40 bg-white rounded-lg shadow-md">
    <div className="w-40 h-40 min-h-40 max-h-40 w-full relative">
      <BringzzShimmer className="w-full h-full rounded-md" />
      <div className="absolute top-0 right-0">
        <BringzzShimmer className="w-8 h-8 rounded-full m-2" />
      </div>
    </div>
    <div className="p-1">
      <div className="px-1">
        <BringzzShimmer className="w-3/4 h-4 mt-2 mb-1" />
        <BringzzShimmer className="w-full h-5 my-1" />
        <div className="flex flex-col">
          <BringzzShimmer className="w-1/2 h-4 my-1" />
          <div className="flex justify-between items-center">
            <BringzzShimmer className="w-1/3 h-4" />
            <BringzzShimmer className="w-1/4 h-5" />
          </div>
        </div>
      </div>
      <div className="h-9 mt-5">
        <BringzzShimmer className="w-full h-full rounded-lg" />
      </div>
    </div>
  </div>
);

const RecommendationItemShimmer = () => (
  <div className="w-full bg-electric-blue rounded-lg p-6">
    <BringzzShimmer className="w-48 h-6 mb-2" />
    <BringzzShimmer className="w-96 h-4 mb-6" />
    <div className="flex space-x-4 overflow-x-auto py-4">
      {[1, 2, 3].map((index) => (
        <ProductBoxShimmer key={index} />
      ))}
    </div>
  </div>
);

	return (
		<div className='px-4 bg-sand pt-14 pb-6'>
			<BringzzText
				tag="h2"
				className="uppercase"
			>
				{t('component.recommendation_box.title')}
			</BringzzText>
			<BringzzBoxSlider className='space-x-4 mt-1 mb-8 py-1'>
				{isLoading ? (
          Array(4).fill(0).map((_, index) => (
            <ProductBoxShimmer key={index} />
          ))
        ) : (
          Array.isArray(featuredProducts) && featuredProducts.map((product) => (
            <BringzzProductBox
              key={product.integrationId}
              className='shadow-md cursor-pointer h-80'
              productId={product.productId}
              image={product.productImage || DefaultProductImage}
              // TODO: Manufacturer
              // manufacturer={product.productName || 'Manufacturer'}
              label={product.productName || 'Product Name'}
              productType={product.type || ''}
              productQuantity={`${product.productAmount || 0} ${product.packagingType?.toLowerCase() || 'unit'}`}
              price={(product.price || 0).toFixed(2).replace('.', ',')}
					onClick={() => navigate(`/product/details/${product.integrationId}`)}
              topRightChildren={
                <BringzzIcon
                  icon={product.isBookmarked ? "IconBookmarkSolid" : "IconBookmark"}
                  folder={product.isBookmarked ? 'HeroIcons' : 'LineIcons'}
                  className='text-midnight-blue p-2'
                  size='20'
                  onClick={(e) => handleBookmark(e, product.integrationId)}
                />
              }
            >
              {user?.userRole !== USER_ROLES.PHARMACY_ROLE && (
                <BringzzCartSelector
                  productId={product.productId}
                  maxQuantity={3}
                  defaultQuantity={1}
                  onCartClick={(productId, quantity) => addToCart(productId, quantity)}
                />
              )}
            </BringzzProductBox>
          ))
        )}
			</BringzzBoxSlider>
			{showQuantityModal && (
				<BringzzBottomDrawer
				isOpen={showQuantityModal}
				close={() => setShowQuantityModal(false)}
				title={t('component.recommendation_box.quantity_modal.title')}
				>
				<div className="p-4">
					<BringzzText className="text-center mb-2">
					{t('component.recommendation_box.quantity_modal.cannot_add', { quantity: currentProduct?.quantity })}
					</BringzzText>
					<BringzzText className="text-center">
					{t('component.recommendation_box.quantity_modal.available', { quantity: currentProduct?.quantity })}
					</BringzzText>
				</div>
				</BringzzBottomDrawer>
			)}

			<BringzzBasketDrawer
				products={addedProduct ? [addedProduct] : []}
				isOpen={isBasketDrawerOpen}
				basketContainerProps={{ className: '' }}
				continueShoppingBtn={handleContinueShopping}
				goToBasketBtn={handleGoToBasket}
			/>
			
      {/* {isLoading ? (
        <RecommendationItemShimmer />
      ) : (
        <BringzzRecommendationItem
          title={t('component.recommendation_box.flu_remedies.title')}
          subTitle={t('component.recommendation_box.flu_remedies.subtitle')}
          className='bg-electric-blue'
          image={RecommendationItemBackground}
        >
          {Array.isArray(featuredProducts) && featuredProducts.map((product) => (
            <BringzzProductBox
              key={product.id}
              className='shadow-md cursor-pointer'
              productId={product.productId}
              image={product.productImage || DefaultProductImage}
              // TODO: Manufacturer
              // manufacturer={product.pharmacyId_Dyn_Sort || 'Manufacturer'}
              label={product.productName || `Product ${product.productId}`}
              productType={product.type || ''}
              productQuantity={`${product.productAmount || 0} ${product.packagingType?.toLowerCase() || 'unit'}`}
              onClick={() => navigate(`/product/details/${product.integrationId}`)}
              price={(product.price || 0).toFixed(2).replace('.', ',')}
              topRightChildren={
                <BringzzIcon
                  icon={product.isBookmarked ? "IconBookmarkSolid" : "IconBookmark"}
                  folder={product.isBookmarked ? 'HeroIcons' : 'LineIcons'}
                  className='text-midnight-blue p-2'
                  size='20'
                  onClick={(e) => handleBookmark(e, product.integrationId)}
                />
              }
            >
              {user?.userRole !== USER_ROLES.PHARMACY_ROLE && (
                <BringzzCartSelector
                  productId={product.productId}
                  maxQuantity={3}
                  defaultQuantity={1}
                  onCartClick={(productId, quantity) => addToCart(productId, quantity)}
                />
              )}
            </BringzzProductBox>
          ))}
        </BringzzRecommendationItem>
      )} */}
			<BringzzBasketDrawer
				products={addedProduct ? [addedProduct] : []}
				isOpen={isBasketDrawerOpen}
				basketContainerProps={{ className: '' }}
				continueShoppingBtn={handleContinueShopping}
				goToBasketBtn={handleGoToBasket}
			/>
			
			{/* Bookmark Collection Drawer */}
			<BringzzBottomDrawer
				isOpen={showBookmarkDrawer}
				close={() => {
					setShowBookmarkDrawer(false);
					setSelectedProductId(null);
					setShowNewCollectionInput(false);
					setNewCollectionName('');
				}}
				title={t('component.bookmarks.select_collection')}
			>
				<div className="px-6 py-4">
					{/* Save without collection */}
					<div 
						className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2"
						onClick={() => addBookmarkToCollection(selectedProductId)}
					>
						<BringzzIcon icon="IconBookmark" folder="LineIcons" size="20" className="mr-3" />
						<BringzzText>{t('component.bookmarks.save_without_collection')}</BringzzText>
					</div>
					
					{/* Existing collections */}
					{collections.map((collection) => (
						<div 
							key={collection.id}
							className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2"
							onClick={() => addBookmarkToCollection(selectedProductId, collection.integrationId)}
						>
							<BringzzIcon icon="IconFolder" folder="LineIcons" size="20" className="mr-3" />
							<BringzzText>{collection.name}</BringzzText>
						</div>
					))}
					
					{/* Create new collection */}
					{showNewCollectionInput ? (
						<div className="p-3 border rounded-lg">
							<BringzzInput
								placeholder={t('component.bookmarks.collection_name')}
								value={newCollectionName}
								onChange={(e) => setNewCollectionName(e.target.value)}
								className="mb-3"
							/>
							<div className="flex space-x-2">
								<BringzzButton
									size="sm"
									onClick={createNewCollection}
									disabled={!newCollectionName.trim()}
								>
									{t('component.bookmarks.create')}
								</BringzzButton>
								<BringzzButton
									size="sm"
									variant="secondary"
									onClick={() => {
										setShowNewCollectionInput(false);
										setNewCollectionName('');
									}}
								>
									{t('component.bookmarks.cancel')}
								</BringzzButton>
							</div>
						</div>
					) : (
						<div 
							className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer border-2 border-dashed border-gray-300"
							onClick={() => setShowNewCollectionInput(true)}
						>
							<BringzzIcon icon="IconPlus" folder="LineIcons" size="20" className="mr-3" />
							<BringzzText>{t('component.bookmarks.create_new_collection')}</BringzzText>
						</div>
					)}
				</div>
			</BringzzBottomDrawer>
			</div>
		);
		};

		export default RecommendationBox;